import { useBubbleGetCandidateStats, useBubbleGetSiteId } from "@crowdcoursing/api/openapi";
import { keepPreviousData } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";

import { type DateRangeValue } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";
import { StudentCandidacyDashboard } from "~ui/templates/AdminTemplate/DashboardStats/StudentCandidacyDashboard";
import { aggregateCandidateStatsByDate } from "~ui/utils/aggregateCandidateStatsByDate";

const START_DATE_STORAGE_KEY = "studentCandidacyTab.startDate";

const getInitialTimeRange = (): DateRangeValue => {
  try {
    const stored = localStorage.getItem(START_DATE_STORAGE_KEY);
    if (stored) {
      const startDate = DateTime.fromISO(stored);
      if (startDate?.isValid) {
        return { endDate: DateTime.now(), startDate };
      }
    }
  } catch {
    // Fall through to default
  }
  return {
    endDate: DateTime.now(),
    startDate: DateTime.now().minus({ months: 1 }),
  };
};

const useStudentCandidacyDashboardProps = () => {
  const { data: bubbleId } = useBubbleGetSiteId({});
  const [timeRange, setTimeRange] = useState<DateRangeValue>(getInitialTimeRange);

  const handleTimeRangeChange = (newTimeRange: DateRangeValue) => {
    setTimeRange(newTimeRange);
    if (newTimeRange.startDate) {
      localStorage.setItem(START_DATE_STORAGE_KEY, newTimeRange.startDate.toISO() ?? "");
    }
  };

  const dateParams = useMemo(
    () => ({
      endDate: timeRange.endDate?.toISO() ?? DateTime.now().toISO(),
      isAggregate: true, // Get aggregate stats from backend, then combine by date on frontend
      startDate: timeRange.startDate?.toISO() ?? DateTime.now().toISO(),
    }),
    [timeRange],
  );

  const { data, status } = useBubbleGetCandidateStats(
    {
      pathParams: { bubbleId: bubbleId ?? "" },
      queryParams: dateParams,
    },
    { enabled: !!bubbleId, placeholderData: keepPreviousData },
  );

  // Aggregate stats by date on the frontend
  const aggregatedMetrics = useMemo(() => aggregateCandidateStatsByDate(data?.stats), [data?.stats]);

  return {
    bubbleId: bubbleId ?? "",
    metrics: aggregatedMetrics,
    onTimeRangeChange: handleTimeRangeChange,
    status,
  };
};

export const StudentCandidacyTab = () => {
  const props = useStudentCandidacyDashboardProps();
  return <StudentCandidacyDashboard {...props} />;
};
